/** Canonical portal model order. Ids match the edge. Empty enabledModels is none, not every model. */
export const UI_MODELS = ["zone", "gesture", "person", "fight", "weapon", "abusive"] as const;

export type UiModel = (typeof UI_MODELS)[number];

const UI_MODEL_SET = new Set<string>(UI_MODELS);

const SENSITIVITY_FIELDS = [
  { model: "zone", key: "zoneST", labelKey: "Zone Sensitivity" },
  { model: "gesture", key: "gestureST", labelKey: "Gesture Sensitivity" },
  { model: "person", key: "passedOutST", labelKey: "Passed Out Sensitivity" },
  { model: "fight", key: "fightST", labelKey: "Fight Sensitivity" },
  { model: "weapon", key: "weaponST", labelKey: "Weapon Sensitivity" },
  { model: "abusive", key: "abusiveST", labelKey: "Abuse Detection Sensitivity" }
] as const;

export type SensitivityField = (typeof SENSITIVITY_FIELDS)[number];

type SiteModels = { enabledModels?: readonly string[] | null };
type ScopeSite = { _id?: string; name?: string; enabledModels?: readonly string[] | null };

/**
 * Models a site may show.
 * A missing list is the legacy "all models" site. An empty list is none.
 */
export function filterModelsForUI(models?: readonly string[] | null): UiModel[] {
  if (Array.isArray(models) && models.length === 0) return [];
  const source = Array.isArray(models) ? models : UI_MODELS;
  const allowed = new Set<string>();
  source.forEach((model) => {
    if (UI_MODEL_SET.has(model)) allowed.add(model);
  });
  return UI_MODELS.filter((model) => allowed.has(model));
}

/** Union, in portal order. Used when a view covers more than one site. */
export function unionEnabledModels(sites?: readonly SiteModels[] | null): UiModel[] {
  const allowed = new Set<string>();
  (sites || []).forEach((site) => {
    filterModelsForUI(site?.enabledModels).forEach((model) => allowed.add(model));
  });
  return UI_MODELS.filter((model) => allowed.has(model));
}

/** Site settings switches. Always the full catalog, never a site's current subset. */
export function adminModelCatalog(): UiModel[] {
  return [...UI_MODELS];
}

export function sensitivityFieldsForModels(models?: readonly string[] | null): SensitivityField[] {
  const enabled = new Set<string>(filterModelsForUI(models));
  return SENSITIVITY_FIELDS.filter((field) => enabled.has(field.model));
}

/** Event-type filter options. One option per enabled model id. */
export function eventTypeFilters(models?: readonly string[] | null): { value: string; models: UiModel[] }[] {
  return filterModelsForUI(models).map((model) => ({ value: model, models: [model] }));
}

export function activeTypeFilter(models: readonly string[] | null | undefined, value?: string): string {
  if (!value || value === "all") return "all";
  const allowed = new Set(eventTypeFilters(models).map((option) => option.value));
  return allowed.has(value) ? value : "all";
}

export function threatsForModels<T extends { threatType?: { model?: string } }>(
  threats: readonly T[] | null | undefined,
  models?: readonly string[] | null,
  typeFilter?: string
): T[] {
  const list = threats ? [...threats] : [];
  const active = activeTypeFilter(models, typeFilter);
  if (active !== "all") {
    const allowed = new Set(
      active
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    );
    return list.filter((threat) => allowed.has(threat?.threatType?.model || ""));
  }
  if (Array.isArray(models)) {
    const allowed = new Set<string>(filterModelsForUI(models));
    return list.filter((threat) => allowed.has(threat?.threatType?.model || ""));
  }
  return list;
}

export function isResolvedSite(site?: ScopeSite | null): boolean {
  if (!site) return false;
  return Boolean(site.name) || Array.isArray(site.enabledModels);
}

/**
 * selectedIds null: every resolved site (union).
 * selectedIds [] or unknown ids: not ready, so the UI does not flash every model.
 */
export function modelScope(
  sites?: readonly ScopeSite[] | null,
  selectedIds?: readonly string[] | null
): { ready: boolean; models: UiModel[] } {
  const resolved = (sites || []).filter((site) => isResolvedSite(site));
  if (selectedIds == null) {
    if (!resolved.length) return { ready: false, models: [] };
    return { ready: true, models: unionEnabledModels(resolved) };
  }
  const ids = selectedIds.map((id) => String(id)).filter(Boolean);
  if (!ids.length || !resolved.length) return { ready: false, models: [] };
  const scope = resolved.filter((site) => site._id && ids.includes(String(site._id)));
  if (!scope.length) return { ready: false, models: [] };
  return { ready: true, models: unionEnabledModels(scope) };
}
